import * as THREE from 'three'
import { createBackFaceCullingLineMaterial } from './utils'

/**
 * LRU cap. Each entry is a MeshStandardMaterial (shader program + uniforms on the
 * GPU plus a few hundred bytes of JS state). Without a cap, navigating across
 * patients / encounters slowly leaks materials forever because the cache is a
 * module-level singleton that outlives the React tree.
 */
const MAX_LINE_MATERIALS = 64

class MaterialCache {
  // Map preserves insertion order; we promote on get for LRU semantics.
  private cache = new Map<string, THREE.MeshStandardMaterial>()
  private pickMaterialCache = new Map<string, THREE.MeshBasicMaterial>()

  getLineMaterial(
    color: string,
    isSelected: boolean,
    isFromSelectedProcedure: boolean,
    isPrinting: boolean,
    isPreview: boolean = false,
    useDepthTest: boolean = false,
    inactiveProcedureOpacity: number = 0,
  ): THREE.MeshStandardMaterial {
    const key = `${color}-${isSelected}-${isFromSelectedProcedure}-${isPrinting}-${isPreview}-${useDepthTest}-${inactiveProcedureOpacity}`

    const existing = this.cache.get(key)
    if (existing) {
      // Promote to most-recently-used by reinserting.
      this.cache.delete(key)
      this.cache.set(key, existing)
      return existing
    }

    const material = createBackFaceCullingLineMaterial(
      color,
      isSelected,
      isFromSelectedProcedure,
      isPrinting,
      isPreview,
      useDepthTest,
      inactiveProcedureOpacity,
    )
    this.cache.set(key, material)

    while (this.cache.size > MAX_LINE_MATERIALS) {
      const oldestKey = this.cache.keys().next().value
      if (oldestKey === undefined) break
      const oldest = this.cache.get(oldestKey)
      this.cache.delete(oldestKey)
      oldest?.dispose()
    }

    return material
  }

  getPickMaterial(): THREE.MeshBasicMaterial {
    const key = 'pick'

    if (!this.pickMaterialCache.has(key)) {
      const material = new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        depthTest: false,
        depthWrite: false,
      })
      this.pickMaterialCache.set(key, material)
    }

    return this.pickMaterialCache.get(key)!
  }

  clear(): void {
    this.cache.forEach((material) => material.dispose())
    this.cache.clear()

    this.pickMaterialCache.forEach((material) => material.dispose())
    this.pickMaterialCache.clear()
  }

  getStats() {
    return {
      lineMaterials: this.cache.size,
      pickMaterials: this.pickMaterialCache.size,
      total: this.cache.size + this.pickMaterialCache.size,
    }
  }
}

export const materialCache = new MaterialCache()
